import { Grid, Stack, Tabs, TabsPanel } from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthDailyStats } from '@/services/api/services-api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { formatThroughput, useWorkspaceHealthSimulation, useEnvHealth } from '@/features/workspace-health';
import { useActiveHealthTab } from '../hooks';
import { AlertsPanel } from './alerts-panel';
import { DataAlertsPanel } from './data-alerts-panel';
import { StatsPanel } from './stats-panel';
import { AttributesPanel } from './attributes-panel';
import { EventsPanel } from './events-panel';
import { SummaryPanel } from './summary-panel';
import { AISummaryPanel } from './ai-summary-panel';
import { RecommendationsPanel } from './recommendations-panel';
import { StatCard } from './stat-card';
import { ServicesTabContent } from './services-tab-content';
import { MessagingTabContent } from './messaging-tab-content';
import { CampaignsPanel } from './campaigns-panel';
import { ApiTriggeredBroadcastsPanel } from './api-triggered-broadcasts-panel';
import { ObjectsPanel } from './objects-panel';
import { TasksPanel } from './tasks-panel';
import * as styles from './index.css';

function outgoingThroughputTooltip(stats: ServicesApiEnvHealthDailyStats): string {
  const base =
    'How fast outgoing messages are being sent right now. This number may be low when there is nothing to send.';
  const total = stats.throughput.egress?.current ?? 0;
  const outgoing = stats.throughput.outgoing?.current ?? 0;
  if (total <= 0 || outgoing <= 0) return base;
  const internalShare = (total - outgoing) / total;
  if (internalShare < 0.2) return base;
  return `${base} Some processing capacity is currently used by internal operations (profile merging, object updates).`;
}

const TABS = [
  { value: 'overview', text: 'Overview' },
  { value: 'services', text: 'Services' },
  { value: 'data', text: 'Data' },
  { value: 'messaging', text: 'Messaging' },
] as const;

export function HealthOverview() {
  const { activeTab, setActiveTab } = useActiveHealthTab();
  const { track } = useAnalytics();

  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();

  const healthQuery = useEnvHealth(workspaceId, simulate);
  const data = healthQuery.data?.body;

  const handleTabChange = (value: string | null) => {
    if (value == null) return;
    const tab = value as typeof activeTab;
    setActiveTab(tab);
    void track('workspaceHealth', 'tabChanged', { Tab: tab });
  };

  return (
    <Grid columns="340px 1fr" gap="300" alignItems="flex-start">
      <Stack gap="200" className={styles.sidebarSticky}>
        <SummaryPanel />
        <RecommendationsPanel />
      </Stack>
      <Tabs tabs={[...TABS]} activeTab={activeTab} onChange={handleTabChange}>
        <TabsPanel value="overview">
          <Stack gap="200" pt="200">
            <AISummaryPanel />
            <AlertsPanel />
            <StatsPanel />
          </Stack>
        </TabsPanel>
        <TabsPanel value="services">
          <Stack pt="200">
            <ServicesTabContent />
          </Stack>
        </TabsPanel>
        <TabsPanel value="data">
          <Stack gap="200" pt="200">
            <Grid columns={3} gap="200">
              <StatCard
                icon="trigger"
                label="Data throughput"
                description="How fast data is being processed right now. This number may be low during the quiet period."
                value={formatThroughput(data?.stats.throughput.ingress?.current ?? 0)
                  .replace('/s', '')
                  .replace('/min', '')}
                unit={(data?.stats.throughput.ingress?.current ?? 0) >= 1 ? '/s' : '/min'}
              />
              <StatCard
                icon="database"
                label="Data processed today"
                description="Total records processed since midnight UTC, including events, profile updates, imports, and more."
                value={(data?.stats.processed_today.total?.current ?? 0).toLocaleString()}
                unit="records"
                currentValue={data?.stats.processed_today.total?.current}
                prevDayValue={data?.stats.processed_today.total?.prev_day}
                processedToday={data?.stats.processed_today}
              />
              <StatCard
                icon="time-delay"
                label="Pending"
                description="Records waiting to be processed across all queues."
                value={
                  [...(data?.processing.categories ?? []), ...(data?.egress.categories ?? [])]
                    ?.reduce((sum, c) => sum + c.pending, 0)
                    ?.toLocaleString() ?? '0'
                }
                unit="records"
              />
            </Grid>
            <DataAlertsPanel />
            <TasksPanel />
            <Grid columns={2} gap="200" alignItems="stretch">
              <AttributesPanel />
              <EventsPanel />
              <ObjectsPanel />
            </Grid>
          </Stack>
        </TabsPanel>
        <TabsPanel value="messaging">
          <Stack gap="200" pt="200">
            <Grid columns={3} gap="200">
              <StatCard
                icon="trigger"
                label="Outgoing throughput"
                description={data?.stats != null ? outgoingThroughputTooltip(data.stats) : ''}
                value={formatThroughput(data?.stats?.throughput.outgoing?.current ?? 0)
                  .replace('/s', '')
                  .replace('/min', '')}
                unit={(data?.stats?.throughput.outgoing?.current ?? 0) >= 1 ? '/s' : '/min'}
              />
              <StatCard
                icon="database"
                label="Sent today"
                description="Total outgoing messages sent since midnight UTC, including email, push, SMS, webhooks, and more."
                value={(data?.stats?.processed_today.outgoing?.current ?? 0).toLocaleString()}
                unit="records"
                currentValue={data?.stats?.processed_today.outgoing?.current}
                prevDayValue={data?.stats?.processed_today.outgoing?.prev_day}
              />
              <StatCard
                icon="time-delay"
                label="Pending"
                description="Outgoing records waiting to be processed."
                value={(
                  data?.egress.categories
                    ?.filter((c) => c.name !== 'related_updates')
                    ?.reduce((sum, c) => sum + c.pending, 0) ?? 0
                ).toLocaleString()}
                unit="records"
              />
            </Grid>
            <MessagingTabContent />
            <Grid columns={2} gap="200" alignItems="stretch">
              <CampaignsPanel />
              <ApiTriggeredBroadcastsPanel />
            </Grid>
          </Stack>
        </TabsPanel>
      </Tabs>
    </Grid>
  );
}
